import { z } from 'zod';
import { CursorPageQuery, IsoDatetime, LangCode, NoteId } from './primitives.js';

/**
 * A note somebody keeps for themselves.
 *
 * Compare `MemoryEntry` in this same folder: a house rule has a category,
 * tags, an audience, a pin and a provenance because it is written FOR other
 * people. A note has a body and a checkbox, because it is written for one.
 * There is deliberately no assignee, no state, no proof and no priority — the
 * moment any of those exist somebody is being managed, and this is the one
 * surface in the product where nobody is.
 *
 * Only the request shapes live here. The response is the `notes` row as
 * drizzle selects it, the same as /v1/memory and /v1/staff-messages hand back
 * theirs — a second, hand-written response schema is one more thing to keep in
 * step with a column list.
 */
export const CreateNoteRequest = z.object({
  // The note itself. Required — a titled note with no body is a heading.
  body: z.string().trim().min(1).max(8000),
  // Optional, because most notes are one line and naming them is a chore.
  title: z.string().trim().min(1).max(200).optional(),
  remind_at: IsoDatetime.optional(),
  source_lang: LangCode.optional(),
  // One per submit — see migration 0078. Lets the offline queue replay this
  // exact POST without leaving two copies of the same thought.
  idempotency_key: z.string().max(64).optional(),
});
export type CreateNoteRequest = z.infer<typeof CreateNoteRequest>;

/**
 * Every field absolute rather than incremental (`done: true`, never
 * `toggle`), which is what makes a PATCH safe to replay off the offline queue:
 * sending it twice lands the same row both times.
 *
 * `.nullable()` on the two optional columns is how "clear this" is said —
 * absent means leave it alone, null means remove it.
 */
export const UpdateNoteRequest = z
  .object({
    body: z.string().trim().min(1).max(8000),
    title: z.string().trim().max(200).nullable(),
    done: z.boolean(),
    remind_at: IsoDatetime.nullable(),
  })
  .partial();
export type UpdateNoteRequest = z.infer<typeof UpdateNoteRequest>;

export const NoteIdParams = z.object({ id: NoteId });

export const ListNotesQuery = CursorPageQuery.extend({
  /**
   * Ticked notes are still yours to read, so they stay in the list by
   * default; `false` is the "just what's left" view. Coerced from the string
   * everything on a querystring actually is.
   */
  include_done: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v !== 'false'),
});
export type ListNotesQuery = z.infer<typeof ListNotesQuery>;
